import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BodyText,
  Button,
  Div,
  Dropdown,
  Flex,
  Gutter,
  Headline,
  Loader,
  ModalDialog,
  OptionListOption,
  SearchInput,
  Title,
} from '@gaiads/telia-react-component-library';
import { useHistory } from 'react-router-dom';

import ProductsList, { ProductsListItem, ProductTableHeaders } from './ProductList/ProductsList';
import {
  deleteProductAndVariants,
  fetchAdminProducts,
  fetchProductVariants,
  selectAdminProductsLoading,
  selectListItems,
  selectPageMetadata,
} from '../../store/adminProducts';
import { selectAllLocales } from '../../store/locales';
import { fetchSalesChannels } from '../../store/salesChannels';
import BottomDrawer from './BottomDrawer';
import { Loading } from '../../utils/store';
import { compareProducts, getSKUsCountByListItems } from '../../utils/products';
import { ListPagination } from '../../shared/ListUtil/Pagination';

import './Products.scss';
import { fetchAdminCategories, selectAllCategories } from '../../store/adminCategories';
import { fetchAdminBrands, selectAllBrands } from '../../store/adminBrands';
import { productServiceAdminPriceRefreshRequestsClient } from '../../api-client/product-service-client';
import { setNotification } from '../../store/ui';
import { selectFeatureFlags } from '../../store/featureFlags';

export default function ProductsView() {
  const { t } = useTranslation('productsscreen');
  const dispatch = useDispatch();
  const history = useHistory();
  const brands = useSelector(selectAllBrands);
  const categories = useSelector(selectAllCategories);
  const listItems = useSelector(selectListItems);
  const loading = useSelector(selectAdminProductsLoading) !== Loading.SUCCEEDED;
  const pageMetadata = useSelector(selectPageMetadata);
  const locales = useSelector(selectAllLocales);
  const maintenanceModeEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_MAINTENANCE_MODE_ENABLED;
  const priceMasterEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_PRICE_MASTER_ENABLED;

  const numberOfItemsPerPageOptions = [20, 50, 100];

  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>(searchValue);
  const [brandFilter, setBrandFilter] = useState<OptionListOption>();
  const [categoryFilter, setCategoryFilter] = useState<OptionListOption>();
  const [selectedProducts, setSelectedProducts] = useState<ProductsListItem[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [openedProducts, setOpenedProducts] = useState<ProductsListItem[]>([]);
  const [loadedProducts, setLoadedProducts] = useState<ProductsListItem[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(numberOfItemsPerPageOptions[0]);
  const [showDeleteProductConfirmation, setShowDeleteProductConfirmation] = useState<boolean>(false);

  const tableHeaders: ProductTableHeaders = [
    undefined,
    undefined,
    'brand',
    'productName',
    undefined,
    'sku',
    'languages',
    'availability',
    'salesChannels',
  ];

  const isSelected = (product: ProductsListItem): boolean => selectedProducts.some(existingProduct => compareProducts(product, existingProduct));
  const isOpened = (product: ProductsListItem): boolean => openedProducts.some(existingProduct => compareProducts(product, existingProduct));
  const isLoaded = (product: ProductsListItem): boolean => loadedProducts.some(existingProduct => compareProducts(product, existingProduct));

  useEffect(() => {
    if (maintenanceModeEnabled) {
      dispatch(
        setNotification({
          text: t('notification.warningMaintenanceModeEnabled'),
          show: true,
          type: 'warning',
        }),
      );
    }
  }, [dispatch, maintenanceModeEnabled, t]);

  useEffect(() => {
    setLoadedProducts([]);
    setOpenedProducts([]);
    dispatch(fetchSalesChannels());
    dispatch(
      fetchAdminProducts({
        goToPageNumber: currentPageNumber,
        numberOfItems: numberOfItems,
        searchText: debouncedSearchValue ? debouncedSearchValue : undefined,
        brand: brandFilter?.value,
        categoryIds: categoryFilter ? [Number.parseInt(categoryFilter.value)] : undefined,
      }),
    );
  }, [dispatch, brandFilter, categoryFilter, currentPageNumber, numberOfItems, debouncedSearchValue]);

  useEffect(() => {
    dispatch(fetchAdminBrands());
    dispatch(fetchAdminCategories());
  }, [dispatch]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      // Update selected variants
      const updatedSelected = listItems.filter(product => isSelected(product));
      setSelectedProducts(updatedSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems]);

  // If new list items appear on select all checkbox then mark all as selected. (e.g. after product variants fetch)
  useEffect(() => {
    if (selectAll && listItems?.length) {
      setSelectedProducts(listItems);
    }
  }, [listItems, selectAll]);

  // Debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  useEffect(() => {
    setCurrentPageNumber(0);
  }, [debouncedSearchValue, brandFilter, categoryFilter]);

  /* Filters event handlers */

  const onChangeCategoryFilter = (_event: SyntheticEvent<HTMLDivElement>, selectedOption: OptionListOption) => {
    setCategoryFilter(selectedOption);
  };

  const onChangeBrandFilter = (_event: SyntheticEvent<HTMLDivElement>, selectedOption: OptionListOption) => {
    setBrandFilter(selectedOption);
  };

  const onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void = event => {
    setSearchValue(event.target.value);
  };

  const onSearchClick = async () => {
    setCurrentPageNumber(0);
    dispatch(
      fetchAdminProducts({
        goToPageNumber: 0,
        numberOfItems: numberOfItems,
        searchText: debouncedSearchValue,
        brand: brandFilter?.value,
        categoryIds: categoryFilter ? [Number.parseInt(categoryFilter.value)] : undefined,
      }),
    );
  };

  const resetSelections = () => {
    setSelectedProducts([]);
    setSelectAll(false);
  };

  const handleRemoveProducts = async () => {
    setShowDeleteProductConfirmation(false);
    if (selectedProducts.length === 0) {
      return;
    }

    const productsWithoutVariants = selectedProducts.filter(product => !product.parent_id);
    const variantsWithoutProducts = selectedProducts.filter(product => product.parent_id);
    // If a product is selected (select all) then filter off all product's variants by id/parent_id
    const variantsWithoutSelectedProductVariants = variantsWithoutProducts.filter(
      variant => !productsWithoutVariants.map(product => product.id).includes(variant.parent_id || -1),
    );
    // Combine the items to be deleted
    const itemsToBeDeleted = [...variantsWithoutSelectedProductVariants, ...productsWithoutVariants];

    resetSelections();

    await dispatch(deleteProductAndVariants(itemsToBeDeleted));
    dispatch(
      fetchAdminProducts({
        goToPageNumber: currentPageNumber,
        numberOfItems: numberOfItems,
        searchText: debouncedSearchValue,
        brand: brandFilter?.value,
        categoryIds: categoryFilter ? [Number.parseInt(categoryFilter.value)] : undefined,
      }),
    );
  };

  /* Checkbox event handlers */

  const handleAllChecked = () => {
    const updatedSelectAll = !selectAll;
    let updatedSelection: ProductsListItem[] = [];

    if (updatedSelectAll) {
      const newLoadedProducts: ProductsListItem[] = [];
      for (const product of listItems) {
        if (product.type === 'MultiVariantProductOverview' && !isLoaded(product) && !product.parent_id) {
          dispatch(fetchProductVariants(product.id));
          newLoadedProducts.push(product);
        }
      }

      setLoadedProducts(previousLoadedProducts => [...previousLoadedProducts.concat(newLoadedProducts)]);
      updatedSelection = [...listItems];
    }

    setSelectAll(updatedSelectAll);
    setSelectedProducts(updatedSelection);
  };

  const handleProductChecked = (product: ProductsListItem) => {
    let updateSelected = [...selectedProducts];

    if (isSelected(product)) {
      // remove main product
      updateSelected = updateSelected.filter(selectProduct => !compareProducts(product, selectProduct));
      if (!product.parent_id && product.variants_count > 1) {
        // remove all product's variants
        const variants = updateSelected.filter(selectProduct => selectProduct.parent_id !== product.id);
        updateSelected = [...variants];
      }
    } else {
      if (!product.parent_id && product.variants_count > 1) {
        // remove all product's variants if exist just in case
        const selectedWithoutVariants = [...updateSelected.filter(selectProduct => selectProduct.parent_id !== product.id)];
        // get all product's variants
        const variants = listItems.filter(productItem => productItem.parent_id === product.id);
        updateSelected = [...selectedWithoutVariants, ...variants];
      }
      // Add only clicked product
      updateSelected.push(product);
    }

    setSelectedProducts(updateSelected);
  };

  // Move to product detail view
  const moveToProductDetail = (product: ProductsListItem) => {
    const { id, parent_id } = product;
    const idToMove = parent_id ? parent_id : id;
    history.push(`/product/${idToMove}/main`);
  };

  // Open or close product's variants section
  const handleOpenRow = async (product: ProductsListItem) => {
    let updateOpened = [...openedProducts];
    if (isOpened(product)) {
      updateOpened = updateOpened.filter(openProduct => !compareProducts(product, openProduct));
    } else {
      updateOpened.push(product);
    }
    setOpenedProducts(updateOpened);

    if (!isLoaded(product)) {
      await dispatch(fetchProductVariants(product.id));
      setLoadedProducts(previousLoadedProducts => [...previousLoadedProducts, product]);
    }
  };

  const TotalCount = ({ count }: { count: number }) => {
    return (
      <Div margin={{ bottom: 'sm' }}>
        <Title tagName="h3" size={'xxs'}>
          {t('product', { count: count })}
        </Title>
      </Div>
    );
  };

  const selectedSKUsCountToDisplay = getSKUsCountByListItems(selectedProducts);

  const clearFilters = () => {
    setBrandFilter(undefined);
    setCategoryFilter(undefined);
    setSearchValue('');
    setDebouncedSearchValue('');
  };

  const handleUpdatePrices = async (): Promise<void> => {
    try {
      await productServiceAdminPriceRefreshRequestsClient.postPriceMasterPriceRefreshRequest();
      dispatch(
        setNotification({
          text: t('notification.refreshPriceMasterPricesSuccessful'),
          show: true,
          type: 'success',
        }),
      );
    } catch {
      dispatch(
        setNotification({
          text: t('notification.refreshPriceMasterPricesFailed'),
          show: true,
          type: 'error',
        }),
      );
    }
  };

  return (
    <Div margin={{ horizontal: 'xxlg', vertical: 'xlg' }}>
      <Flex spaceBetween>
        <Headline size={'md'} padding={{ horizontal: 'xxxs' }}>
          {t('headerTitle')}
        </Headline>
        <Flex>
          {priceMasterEnabled && (
            <Button iconName="sync" variant="secondary" size="md" onClick={handleUpdatePrices} margin={{ right: 'md' }}>
              {t('refreshPriceMasterButton')}
            </Button>
          )}
          <Button iconName="plus" variant="primary" size="md" onClick={() => history.push('/product')}>
            {t('addproductbutton')}
          </Button>
        </Flex>
      </Flex>
      <Gutter size={'xlg'} />
      <Div>
        <Flex centeredVertically>
          <Div flexItem={{ sizeMultiplier: 4, alignSelfTo: 'bottom' }} padding={{ right: 'xlg' }}>
            <SearchInput placeholder={t('filter.textsearchplaceholder')} value={searchValue} onChange={onSearchChange} onIconClick={onSearchClick} />
          </Div>
          <Div flexItem={{ sizeMultiplier: 3, alignSelfTo: 'bottom' }} padding={{ right: 'xs' }}>
            <Flex>
              <Div flexItem={{ sizeMultiplier: 1 }} margin={{ horizontal: 'md' }}>
                <Dropdown
                  showCustomLabel={true}
                  onChange={onChangeCategoryFilter}
                  options={categories.map(category => ({
                    label: category.name,
                    value: `${category.id}`,
                  }))}
                  selectedOption={categoryFilter}
                  placeholder={t('filter.categoryPlaceholder')}
                />
              </Div>
              <Div flexItem={{ sizeMultiplier: 1 }}>
                <Dropdown
                  showCustomLabel={true}
                  onChange={onChangeBrandFilter}
                  options={brands.map(brand => ({
                    label: brand,
                    value: brand,
                  }))}
                  selectedOption={brandFilter}
                  placeholder={t('filter.brandfilterplaceholder')}
                />
              </Div>
            </Flex>
          </Div>
          <Div alignTextToRight flexItem={{ sizeMultiplier: 1 }}>
            <Button variant={'secondary'} size={'md'} onClick={clearFilters}>
              {t('filter.clearFilters')}
            </Button>
          </Div>
        </Flex>
        <Gutter size="xlg" />
        <Div>{!loading && <TotalCount count={pageMetadata.total_items} />}</Div>
        <Div padding={{ horizontal: 'xxxs' }}>
          {loading ? (
            <Div alignTextToCenter padding={{ top: 'xlg' }}>
              <Loader size="lg" />
            </Div>
          ) : (
            <ProductsList
              products={listItems}
              locales={locales}
              tableHeaders={tableHeaders}
              onAllChecked={handleAllChecked}
              onProductChecked={handleProductChecked}
              onClickProductRow={moveToProductDetail}
              onOpenProductRow={handleOpenRow}
              isSelected={isSelected}
              isOpened={isOpened}
              isLoaded={isLoaded}
              selectAll={selectAll}
            />
          )}
        </Div>
        <Gutter size="lg" />
        {!loading && (
          <ListPagination
            pageMetadata={pageMetadata}
            showText={t('pagination.show')}
            numberOfItemsPerPageOptions={numberOfItemsPerPageOptions}
            currentPageNumber={currentPageNumber}
            onPageNumberChange={setCurrentPageNumber}
            numberOfItems={numberOfItems}
            onNumberOfItemsChange={setNumberOfItems}
          />
        )}
      </Div>
      {selectedProducts.length > 0 && (
        <BottomDrawer
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          moveToProductDetail={moveToProductDetail}
          handleRemoveProducts={() => setShowDeleteProductConfirmation(true)}
          resetSelections={resetSelections}
        />
      )}
      <ModalDialog
        title={t('delete_product_confirmation.title', { count: selectedSKUsCountToDisplay })}
        closeButtonText={t('delete_product_confirmation.buttons.cancel')}
        acceptButtonText={t('delete_product_confirmation.buttons.delete')}
        isOpen={showDeleteProductConfirmation}
        onCloseClick={() => setShowDeleteProductConfirmation(false)}
        onAcceptClick={() => handleRemoveProducts()}
      >
        <BodyText>{t('delete_product_confirmation.content', { count: selectedSKUsCountToDisplay })}</BodyText>
      </ModalDialog>
    </Div>
  );
}
